import store from '@polleverywhere/redux'
import { singularize } from '@polleverywhere/core/lib/util'
import { loadPresenter } from './util/loadPresenter'
import activityFactory from '@polleverywhere/graphql-models/lib/activityFactory'
import PresenterRelationship from '@polleverywhere/core/lib/models/presenter_relationship'
import ResponsePageState from './responsePage'
import RegisterFlowState from './registerFlow'
import { slice as responseHistorySlice } from '@polleverywhere/redux/slices/participant_response_history'
import { recentPresentations } from './util/recentPresentations'
import { NotFoundError } from './util/NotFoundError'

/**
 * Forces the specified activity model to act as a participant instance.
 * To ensure results generate the correct request data when being submitted.
 * @param {Backbone.Model} model MQA or SQA activity model
 */
function makeParticipantInstance (model) {
  model.unset('presenter_key')
  if (model.polls) {
    model.polls.forEach((poll) => {
      poll.unset('presenter_key')
    })
  }
}

function setupRelationship (presenterId, participantId) {
  const relationship = new PresenterRelationship({
    presenter_id: presenterId,
    participant_id: participantId
  })
  // Store the initial fetch promise on the model so we can detect when it loaded
  relationship._initialRelationshipFetch = relationship.fetch()

  return relationship
}

async function loadParticipantInstance (type, participantKey) {
  type = singularize(type)
  const model = await activityFactory({
    type,
    participant_key: participantKey
  })
  makeParticipantInstance(model)
  try {
    await model.fetch({
      data: {
        exclude: 'results,flash_options,visual_settings',
        include: type === 'survey' ? 'poll_instances' : undefined
      }
    })
  } catch (e) {
    if (e?.status === 404) {
      throw new NotFoundError('Invalid activity instance')
    } else {
      throw e
    }
  }
  return model
}

export const PAGE_STATES = {
  live_response: async function loadLiveResponsePageState ({ username }) {
    const presenter = await loadPresenter(username)
    const relationship = setupRelationship(presenter.id, this.participant.id)

    recentPresentations.add(username)

    return {
      activePresenter: presenter,
      pageState: new ResponsePageState({
        role: this.role,
        user: this.user,
        presenter,
        participant: this.participant,
        relationship
      })
    }
  },
  static_response: async function loadStaticResponsePageState ({ type, participantKey }) {
    const model = await loadParticipantInstance(type, participantKey)
    const username = model.get('presenter_login') || model.get('owner_login')
    const presenter = await loadPresenter(username)
    const relationship = setupRelationship(presenter.id, this.participant.id)

    return {
      activePresenter: presenter,
      pageState: new ResponsePageState({
        role: this.role,
        user: this.user,
        presenter,
        participant: this.participant,
        relationship,
        currentActivity: model,
        disablePinnedActivity: true
      })
    }
  },
  register: async function loadRegisterFlowPageState ({ username }) {
    const presenter = await loadPresenter(username)

    return {
      activePresenter: presenter,
      pageState: new RegisterFlowState({
        user: this.user,
        presenter,
        participant: this.participant,
        session: this.session
      })
    }
  },
  response_history: function ({ presenter, detail, page }) {
    store.addReducers({ [responseHistorySlice.name]: responseHistorySlice.reducer })

    if (detail) {
      store.dispatch(responseHistorySlice.focusResponse(detail))
    } else if (presenter) {
      store.dispatch(responseHistorySlice.filterPresenter(presenter))
    } else {
      store.dispatch(responseHistorySlice.fetchResponses(page || 1))
    }

    return {}
  }
}
